import React from 'react';
import ProfessionalMan from '../../../assets/images/meeting.png';
import ScrollReveal from '../../../components/ScrollReveal';

const AdaptToYourNeeds = () => {
  const benefits = [
    {
      title: 'Right-Sized Teams',
      description: "Scale from one developer to a full squad as your project grows.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      color: 'from-primary-500 to-primary-600',
    },
    {
      title: 'Instant Onboarding',
      description: "Pre-vetted engineers ready to contribute from day one.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      color: 'from-accent-orange to-accent-pink',
    },
    {
      title: 'Time Zone Aligned',
      description: "Real-time collaboration during your business hours.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'from-accent-cyan to-primary-500',
    },
    {
      title: 'Scale On Demand',
      description: "Ramp up or down as priorities shift. No long-term lock-in.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      color: 'from-accent-purple to-accent-pink',
    },
  ];

  return (
    <section id="solutions" className="section-py bg-white relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-grid opacity-30" />

      <div className="container-lg relative">
        {/* Header */}
        <ScrollReveal>
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
            <span className="label mb-4 block">Flexible Engagement</span>
            <h2 className="heading-lg mb-6">
              Built around
              <span className="text-gradient"> how you work</span>
            </h2>
            <p className="subheading">
              Your business is unique. Our engagement models adapt to your workflow, timeline, and budget.
            </p>
          </div>
        </ScrollReveal>

        {/* Main content */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Image side */}
          <ScrollReveal animation="fade-right" className="relative order-2 lg:order-1">
            <div className="absolute -inset-4 bg-gradient-to-br from-primary-100 via-accent-purple/10 to-accent-cyan/10 rounded-3xl" />
            <div className="relative">
              <img
                src={ProfessionalMan}
                alt="Team collaboration"
                className="rounded-2xl shadow-elevated w-full h-auto"
              />

              {/* Floating card - hidden on mobile, adjusted positioning on tablet */}
              <div className="hidden sm:block absolute -bottom-4 right-2 md:-bottom-8 md:-right-8 bg-white rounded-2xl shadow-elevated p-4 md:p-6 max-w-[220px] md:max-w-xs animate-float">
                <div className="flex items-center gap-3 md:gap-4 mb-2 md:mb-3">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-primary-400 to-accent-purple rounded-full border-2 border-white" />
                    ))}
                  </div>
                  <span className="text-xs md:text-sm font-semibold text-dark-600">+20 Engineers</span>
                </div>
                <p className="text-xs md:text-sm text-dark-500">Ready to join your project today</p>
              </div>
            </div>
          </ScrollReveal>

          {/* Benefits side */}
          <div className="order-1 lg:order-2 space-y-6">
            {benefits.map((benefit, index) => (
              <ScrollReveal key={benefit.title} animation="fade-left" delay={index * 100}>
                <div className="group card card-interactive p-6 flex items-start gap-5">
                <div className={`flex-shrink-0 icon-box-lg bg-gradient-to-br ${benefit.color} text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  {benefit.icon}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-dark-900 mb-1 group-hover:text-primary-600 transition-colors">
                    {benefit.title}
                  </h3>
                  <p className="text-dark-500">
                    {benefit.description}
                  </p>
                </div>
                </div>
              </ScrollReveal>
            ))}

            <ScrollReveal animation="fade-left" delay={400}>
              <div className="pt-4">
                <a href="#contact" className="btn btn-primary btn-lg group">
                  <span className="relative z-10">Discuss Your Needs</span>
                  <svg className="w-5 h-5 relative z-10 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdaptToYourNeeds;
